import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Container,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    ArrowForwardRounded,
    GroupWorkRounded,
    PeopleAltRounded,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
    corporate_addCorporateGroups,
    corporate_deleteCorporateGroup,
    corporate_getCorporateGroups,
} from "../../network/api/corporate.api";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { useNavigate } from "react-router-dom";
import _GroupAddModal from "./partials/_GroupAddModal";
import _GroupDeleteModal from "./partials/_GroupDeleteModal";
import _GroupTableRow from "./partials/_GroupTableRow";

const Groups = () => {
    const error = useContext(ErrorContext);
    const navigate = useNavigate();
    const corporate = useSelector(({ corporate }) => corporate.corporate);
    const [groups, setGroups] = useState(null);
    const [openModalAdd, setOpenModalAdd] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [itemToDelete, setItemToDelete] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        getCorporateGroups();
    }, []);

    function getCorporateGroups() {
        if (!corporate?.Id) return;
        setGroups(null);
        corporate_getCorporateGroups({ Id: corporate.Id }).then(result => {
            setGroups(result.data.Data || []);
        }).catch(e => {
            setGroups([]);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در دریافت لیست گروه‌ها" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    function handleAddGroup(e) {
        e.preventDefault();
        const trimmedName = newGroupName.trim();
        if (!trimmedName) {
            error.showError({ message: "لطفاً نام گروه را وارد کنید" });
            return;
        }

        if ((corporate?.Status === "DEMO" || corporate?.Status === "SECURE_DEMO") && (groups?.length || 0) > 1) {
            error.showError({ message: "در نسخه نمایشی (Demo) حداکثر ۲ گروه امکان‌پذیر است" });
            return;
        }

        setSubmitting(true);
        corporate_addCorporateGroups({
            Name: trimmedName,
            CorporateId: corporate?.Id,
        }).then(() => {
            setSubmitting(false);
            setOpenModalAdd(false);
            setNewGroupName("");
            getCorporateGroups();
        }).catch(e => {
            setSubmitting(false);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در افزودن گروه" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    function handleDeleteGroup(e) {
        e.preventDefault();
        if (!itemToDelete?.Id) return;

        setSubmitting(true);
        corporate_deleteCorporateGroup({ Id: itemToDelete.Id }).then(() => {
            setSubmitting(false);
            setItemToDelete(null);
            getCorporateGroups();
        }).catch(e => {
            setSubmitting(false);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در حذف گروه" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    const isLoading = groups === null;
    const groupList = groups || [];

    return (
        <Container maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
            <title>دسته‌بندی و تگ‌های پرسنل | جیم‌پین</title>

            {/* Executive Page Header */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3.5,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title="بازگشت به لیست پرسنل">
                        <IconButton
                            onClick={() => navigate("/personnel")}
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'action.hover',
                                '&:hover': { bgcolor: 'action.selected' },
                            }}
                        >
                            <ArrowForwardRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 20px rgba(45, 107, 226, 0.3)',
                        }}
                    >
                        <GroupWorkRounded sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            دسته‌بندی و تگ‌های پرسنل
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            گروه‌بندی کارکنان جهت تخصیص هدفمند اعتبارهای رفاهی
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<AddRounded sx={{ ml: 1, mr: -0.5 }} />}
                    onClick={() => {
                        setNewGroupName("");
                        setOpenModalAdd(true);
                    }}
                    sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.2,
                        fontWeight: 700,
                        boxShadow: '0 4px 14px rgba(45, 107, 226, 0.3)',
                    }}
                >
                    تعریف گروه جدید
                </Button>
            </Box>

            {/* Groups Table Card */}
            <Card
                elevation={0}
                sx={{
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 14px rgba(0, 0, 0, 0.03)',
                    overflow: 'hidden',
                }}
            >
                <TableContainer>
                    <Table sx={{ minWidth: 500 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)' }}>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    نام گروه
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    تعداد اعضا
                                </TableCell>
                                <TableCell align="left" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.85rem', width: 120 }}>
                                    عملیات
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={36} thickness={4} sx={{ color: 'primary.main', mb: 1.5 }} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            در حال دریافت لیست گروه‌ها...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : groupList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                bgcolor: 'rgba(28, 139, 156, 0.08)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 2,
                                                color: 'primary.main',
                                            }}
                                        >
                                            <GroupWorkRounded sx={{ fontSize: 32 }} />
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                                            هیچ گروهی ثبت نشده است
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360, mx: 'auto', mb: 2 }}>
                                            شما می‌توانید برای دسته‌بندی پرسنل سازمان خود، اولین گروه را ایجاد کنید.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddRounded sx={{ ml: 0.8 }} />}
                                            onClick={() => setOpenModalAdd(true)}
                                            sx={{ borderRadius: 2.5, fontWeight: 700 }}
                                        >
                                            ایجاد گروه جدید
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                groupList.map((row) => (
                                    <_GroupTableRow
                                        key={row.Id}
                                        row={row}
                                        onDeleteClick={setItemToDelete}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Modals */}
            <_GroupAddModal
                open={openModalAdd}
                onClose={() => setOpenModalAdd(false)}
                onSubmit={handleAddGroup}
                submitting={submitting}
                groupName={newGroupName}
                setGroupName={setNewGroupName}
            />

            <_GroupDeleteModal
                open={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onSubmit={handleDeleteGroup}
                submitting={submitting}
                itemToDelete={itemToDelete}
            />
        </Container>
    );
};

export default Groups;
